import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { servicesAPI, statsAPI } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Store, 
  Plus, 
  Search, 
  ShoppingCart,
  Briefcase,
  Palette,
  Code,
  PenTool,
  Scale,
  MessageSquare
} from 'lucide-react';

const categoryIcons = {
  'Marketing': Briefcase,
  'Accounting': Scale,
  'Design': Palette,
  'Development': Code,
  'Legal': Scale,
  'Consulting': MessageSquare,
  'Writing': PenTool,
  'Other': Briefcase
};

export const MarketplacePage = () => {
  const { user, refreshUser } = useAuth();
  const [services, setServices] = useState([]);
  const [myServices, setMyServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [purchaseMessage, setPurchaseMessage] = useState('');
  const [newService, setNewService] = useState({
    title: '',
    description: '',
    category: '',
    price_points: 100
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadServices();
  }, [selectedCategory]);

  const loadData = async () => {
    try {
      const [servicesRes, categoriesRes, myServicesRes] = await Promise.all([
        servicesAPI.getAll(),
        statsAPI.getCategories(),
        servicesAPI.getMy()
      ]);
      setServices(servicesRes.data);
      setCategories(categoriesRes.data.service_categories);
      setMyServices(myServicesRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadServices = async () => {
    try {
      const params = selectedCategory !== 'all' ? { category: selectedCategory } : {};
      const response = await servicesAPI.getAll(params);
      setServices(response.data);
    } catch (error) {
      console.error('Failed to load services:', error);
    }
  };

  const handleCreateService = async () => {
    if (!newService.title || !newService.description || !newService.category) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      await servicesAPI.create(newService);
      toast.success('Service listed successfully!');
      setCreateDialogOpen(false);
      setNewService({ title: '', description: '', category: '', price_points: 100 });
      loadData();
    } catch (error) {
      toast.error('Failed to create service');
    }
  };

  const handlePurchase = async () => {
    if (!selectedService) return;

    try {
      await servicesAPI.purchase(selectedService.id, { message: purchaseMessage });
      toast.success('Service purchased! The provider will be notified.');
      setPurchaseDialogOpen(false);
      setSelectedService(null);
      setPurchaseMessage('');
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to purchase service');
    }
  };

  const handleDeleteService = async (serviceId) => {
    try {
      await servicesAPI.delete(serviceId);
      toast.success('Service removed');
      loadData();
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  const openPurchaseDialog = (service) => {
    setSelectedService(service);
    setPurchaseDialogOpen(true);
  };

  const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';

  const filteredServices = services.filter(service => 
    service.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    service.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="marketplace-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl text-slate-900">
            Services Marketplace
          </h1>
          <p className="text-slate-600 mt-1">
            Use your points to access business services or offer your expertise.
          </p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gradient-secondary text-white btn-animate" data-testid="offer-service-btn">
              <Plus className="w-5 h-5 mr-2" />
              Offer a Service
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Offer Your Service</DialogTitle>
              <DialogDescription>
                List your skills and set a price in points.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <Input
                placeholder="Service title"
                value={newService.title}
                onChange={(e) => setNewService(prev => ({ ...prev, title: e.target.value }))}
                data-testid="service-title-input"
              />
              <Textarea
                placeholder="Describe what you offer..."
                value={newService.description}
                onChange={(e) => setNewService(prev => ({ ...prev, description: e.target.value }))}
                data-testid="service-description-input"
              />
              <Select 
                value={newService.category}
                onValueChange={(value) => setNewService(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger data-testid="service-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="space-y-2">
                <label className="text-sm text-slate-700">Price (Points)</label>
                <Input
                  type="number"
                  min="10"
                  value={newService.price_points}
                  onChange={(e) => setNewService(prev => ({ ...prev, price_points: parseInt(e.target.value) || 10 }))}
                  data-testid="service-price-input"
                />
              </div>
              <Button onClick={handleCreateService} className="w-full" data-testid="create-service-btn">
                List Service
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Points Balance */}
      <Card className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm">Your Balance</p>
              <p className="font-impact text-4xl">{user?.points || 0} PTS</p>
            </div>
            <Store className="w-12 h-12 text-orange-200" />
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="browse" className="space-y-6">
        <TabsList>
          <TabsTrigger value="browse" data-testid="browse-tab">Browse Services</TabsTrigger>
          <TabsTrigger value="my-services" data-testid="my-services-tab">My Services ({myServices.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="browse" className="space-y-6">
          {/* Search and Filter */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search services..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="service-search-input"
              />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-full sm:w-48" data-testid="service-category-filter">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Services Grid */}
          {filteredServices.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Store className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No services found</h3>
                <p className="text-slate-600">Be the first to offer a service in this category!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredServices.map((service) => {
                const Icon = categoryIcons[service.category] || Briefcase;
                const canPurchase = service.provider_id !== user?.id && user?.points >= service.price_points;
                
                return (
                  <Card key={service.id} className="service-card card-hover" data-testid={`service-${service.id}`}>
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
                          <Icon className="w-6 h-6 text-indigo-600" />
                        </div>
                        <Badge className="bg-orange-100 text-orange-700 font-impact text-lg">
                          {service.price_points} PTS
                        </Badge>
                      </div>
                      
                      <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">
                        {service.title}
                      </h3>
                      <p className="text-slate-600 text-sm line-clamp-3 mb-4">
                        {service.description}
                      </p>
                      
                      <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                        <div className="flex items-center gap-2">
                          <Avatar className="w-6 h-6">
                            <AvatarFallback className="bg-indigo-100 text-indigo-600 text-xs">
                              {getInitials(service.provider_name)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-sm text-slate-600">{service.provider_name}</span>
                        </div>
                        
                        {service.provider_id !== user?.id && (
                          <Button 
                            size="sm"
                            disabled={!canPurchase}
                            onClick={() => openPurchaseDialog(service)}
                            data-testid={`purchase-${service.id}`}
                          >
                            <ShoppingCart className="w-4 h-4 mr-1" />
                            {canPurchase ? 'Get' : 'Not enough pts'}
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="my-services" className="space-y-6">
          {myServices.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No services listed</h3>
                <p className="text-slate-600 mb-4">Start offering your skills to earn points!</p>
                <Button onClick={() => setCreateDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  List Your First Service
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {myServices.map((service) => {
                const Icon = categoryIcons[service.category] || Briefcase;
                
                return (
                  <Card key={service.id} className="card-hover">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
                          <Icon className="w-6 h-6 text-indigo-600" />
                        </div>
                        <Badge className="bg-orange-100 text-orange-700 font-impact text-lg">
                          {service.price_points} PTS
                        </Badge>
                      </div>
                      
                      <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">
                        {service.title}
                      </h3>
                      <Badge variant="secondary" className="mb-2">{service.category}</Badge>
                      <p className="text-slate-600 text-sm line-clamp-3 mb-4">
                        {service.description}
                      </p>
                      
                      <Button 
                        variant="destructive" 
                        size="sm" 
                        className="w-full"
                        onClick={() => handleDeleteService(service.id)}
                      >
                        Remove Listing
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Purchase Dialog */}
      <Dialog open={purchaseDialogOpen} onOpenChange={setPurchaseDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Purchase Service</DialogTitle>
            <DialogDescription>
              Confirm your purchase of "{selectedService?.title}"
            </DialogDescription>
          </DialogHeader>
          {selectedService && (
            <div className="space-y-4 pt-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-slate-600">Service Price</span>
                  <span className="font-impact text-orange-500 text-xl">{selectedService.price_points} PTS</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Your Balance After</span>
                  <span className="font-medium">{(user?.points || 0) - selectedService.price_points} PTS</span>
                </div>
              </div>
              <Textarea
                placeholder="Add a message for the provider (optional)"
                value={purchaseMessage}
                onChange={(e) => setPurchaseMessage(e.target.value)}
              />
              <Button onClick={handlePurchase} className="w-full gradient-primary text-white" data-testid="confirm-purchase-btn">
                Confirm Purchase
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MarketplacePage;
